import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import { CardItem } from '@news-mono/web-common'
import React, { Fragment, ReactNode } from 'react'
import numberToWords from 'number-to-words'
import { useViewport } from './use-viewport'

export type InterlacedComponent = {
    /** The component to interlace. */
    component: (index: number) => ReactNode
    /** After how many cards the first instance of this component appears. Defaults to 1. */
    initialAdOffset?: number
    /** How many cards between each instance of this component. Defaults to 1. */
    cardsBetweenAds?: number
    /**
     * How many cards between each instance of this component on tablet screens.
     * e.g. TheNightly Top Stories uses 6 cards between ads on tablets instead of 5 on desktop.
     */
    cardsBetweenAdsOnTablets?: number
    /** How many times to interlace this component. Defaults to 1. */
    repetitions?: number
}

export const useInterlacedAds = ({
    interlacedComponents = {},
    items,
    renderCard,
    enableInterlacedAd,
}: {
    interlacedComponents: Record<string, InterlacedComponent> | undefined
    items: MaybeLoaded<CardItem>[]
    renderCard: (item: MaybeLoaded<CardItem>, index: number) => ReactNode
    enableInterlacedAd: boolean
}) => {
    const { isTablet } = useViewport()

    // Keep track of how many times left to repeat.
    const interlacedRepetitionsRemaining: Record<string, number> = {}

    for (const [id, interlacedComponent] of Object.entries(
        interlacedComponents,
    )) {
        // Display once if no repetitions specified.
        interlacedRepetitionsRemaining[id] =
            interlacedComponent.repetitions ?? 1
    }
    const listResult: ReactNode[] = []

    for (let i = 0; i < items.length; i++) {
        // Insert interlaced components.
        for (const [id, interlacedComponent] of Object.entries(
            interlacedComponents,
        )) {
            const cardsBetweenAds =
                interlacedComponent.cardsBetweenAdsOnTablets && isTablet
                    ? interlacedComponent.cardsBetweenAdsOnTablets
                    : interlacedComponent.cardsBetweenAds
            const initialAdOffset =
                interlacedComponent.cardsBetweenAdsOnTablets && isTablet
                    ? interlacedComponent.cardsBetweenAdsOnTablets
                    : interlacedComponent.initialAdOffset

            if (
                enableInterlacedAd &&
                isValidInterlaceIndex(i, initialAdOffset, cardsBetweenAds) &&
                interlacedRepetitionsRemaining[id] !== 0
            ) {
                listResult.push(
                    <Fragment key={`${i}-${id}`}>
                        {interlacedComponent.component(i)}
                    </Fragment>,
                )
                interlacedRepetitionsRemaining[id]--
            }
        }

        // Insert card.
        const loadableItem = items[i]

        listResult.push(renderCard(loadableItem, i))
    }

    return listResult
}

/** Checks if a 0-indexed position is valid, provided an initial offset and spaces between. */
const isValidInterlaceIndex = (
    index: number,
    initialOffset = 1,
    spotsBetween = 1,
) =>
    // Sanity checks.
    initialOffset > 0 &&
    spotsBetween > 0 &&
    // Interlaced components can only be between cards.
    index !== 0 &&
    // Actual condition. Object.is() distinguishes between -0 and 0
    Object.is((index - initialOffset) % spotsBetween, 0)

export const getAdUnitId = (
    index: number,
    initialAdOffset: number,
    cardsBetweenAds: number,
) =>
    `topic-multisize-${numberToWords
        .toWords(Math.ceil((initialAdOffset + index) / cardsBetweenAds))
        .replace(/\s+/g, '-')}`
